import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleForgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
      setResetEmailSent(true);
      setResetEmail(email);
    } catch (error) {
      console.error('Password reset failed:', error);
    }
  };

  if (resetEmailSent) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          {/* Logo and name at the top */}
          <div className="text-center mb-4">
            <Link to="/" className="navbar-brand navbar-brand-autodark d-inline-flex justify-content-center">
              <img src="/favicon.svg" alt="BidMatch Logo" width="48" height="48" className="me-2" />
              <span className="brand-text d-flex align-items-center">bidmatch.ai</span>
            </Link>
          </div>
          
          <div className="card card-md">
            <div className="card-body text-center py-4 p-sm-5">
              <span className="avatar avatar-xl mb-3 bg-primary-lt">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-mail" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"></path>
                  <path d="M3 7l9 6l9 -6"></path>
                </svg>
              </span>
              <h1 className="mt-5">Check your email</h1>
              <p className="text-muted">
                We've sent a password reset link to <strong>{resetEmail}</strong>.<br />
                Please check your inbox and click the link to reset your password.
              </p>
              <div className="mt-4">
                <Link to="/login" className="btn btn-primary">Back to Login</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        {/* Logo and name at the top */}
        <div className="text-center mb-4">
          <Link to="/" className="navbar-brand navbar-brand-autodark d-inline-flex justify-content-center">
            <img src="/favicon.svg" alt="BidMatch Logo" width="48" height="48" className="me-2" />
            <span className="brand-text d-flex align-items-center">bidmatch.ai</span>
          </Link>
        </div>
        
        <div className="card card-md">
          <div className="card-body">
            {!showForgotPassword ? (
              <>
                <h2 className="h2 text-center mb-4">Login to your account</h2>
                <Formik
                  initialValues={{ email: '', password: '' }}
                  validationSchema={Yup.object({
                    email: Yup.string().email('Invalid email address').required('Required'),
                    password: Yup.string().required('Required'),
                  })}
                  onSubmit={async (values, { setSubmitting, setStatus }) => {
                    try {
                      await authService.login(values);
                      navigate('/dashboard');
                    } catch (error) {
                      setStatus('Invalid email or password');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ errors, touched, handleChange, status, isSubmitting, values }) => (
                    <Form>
                      <div className="mb-3">
                        <label className="form-label">Email address</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          onChange={handleChange}
                        />
                        {errors.email && touched.email && (
                          <div className="invalid-feedback d-block">{errors.email}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Password</label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          onChange={handleChange}
                        />
                        {errors.password && touched.password && (
                          <div className="invalid-feedback d-block">{errors.password}</div>
                        )}
                      </div>
                      <div className="mb-2">
                        <a href="#" onClick={(e) => {
                          e.preventDefault();
                          setResetEmail(values.email || '');
                          setShowForgotPassword(true);
                        }} className="float-end">
                          Forgot password?
                        </a>
                      </div>
                      {status && <div className="alert alert-danger">{status}</div>}
                      <div className="form-footer">
                        <button 
                          type="submit" 
                          className="btn btn-primary w-100" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Signing in...
                            </>
                          ) : (
                            'Sign in'
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            ) : (
              <>
                <h2 className="h2 text-center mb-4">Forgot Password</h2>
                <Formik
                  initialValues={{ email: resetEmail }}
                  validationSchema={Yup.object({
                    email: Yup.string().email('Invalid email address').required('Required'),
                  })}
                  onSubmit={async (values, { setSubmitting }) => {
                    await handleForgotPassword(values.email);
                    setSubmitting(false);
                  }}
                >
                  {({ errors, touched, handleChange, isSubmitting, values }) => (
                    <Form>
                      <div className="mb-3">
                        <label className="form-label">Email address</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          defaultValue={resetEmail}
                          onChange={handleChange}
                        />
                        {errors.email && touched.email && (
                          <div className="invalid-feedback d-block">{errors.email}</div>
                        )}
                      </div>
                      <div className="form-footer">
                        <button 
                          type="submit" 
                          className="btn btn-primary w-100" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Sending...
                            </>
                          ) : (
                            'Send Reset Link'
                          )}
                        </button>
                      </div>
                      <div className="mt-3 text-center">
                        <a href="#" onClick={(e) => {
                          e.preventDefault();
                          setShowForgotPassword(false);
                        }}>
                          Back to login
                        </a>
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </div>
        </div>
        <div className="text-center text-muted mt-3">
          Don't have an account yet? <Link to="/register" tabIndex={-1}>Sign up</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;